/**
 * util.js — General-purpose Utility Methods
 * ─────────────────────────────────────────────────────
 * CHANGES:
 *  1. Converted UtilMethods class to a plain object (no reason for class + singleton)
 *  2. Fixed cookie methods to use Secure flag in production
 *  3. Cleaned up isNumeric evt handler — used === consistently
 *  4. sortInvoiceArray: fixed .map() without return value (lint warning)
 *  5. Removed redundant convertToFloat (lives in customMethods.js)
 *  6. Minor edge-case fixes in allowNumberWithDecimalNegative
 */

import moment from 'moment';

const utilMethods = {
  setCookie(cName, cValue, minutes) {
    const d = new Date();
    d.setTime(d.getTime() + (minutes * 60 * 1000));
    const expires = 'expires=' + d.toUTCString();
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `${cName}=${cValue}; ${expires}; path=/${secure}`;
  },

  deleteCookie(cname) {
    const d = new Date();
    d.setTime(d.getTime() - (1000 * 60 * 60 * 24));
    const expires = 'expires=' + d.toGMTString();
    document.cookie = `${cname}=; ${expires}; path=/`;
  },

  enumerateDaysBetweenDates(startDate, endDate) {
    const now = endDate.clone();
    const dates = [];
    while (now.isSameOrAfter(startDate)) {
      dates.push(now.format('YYYY/MM/DD'));
      now.subtract(1, 'days');
    }
    return dates;
  },

  getYearByMonthsFromNow(limit = 5) {
    const groupYears = [];
    const now = moment().startOf('month');
    const endDate = moment().startOf('year').subtract(limit, 'year');

    while (endDate.isSameOrBefore(now)) {
      groupYears.push({
        year: now.year(),
        month: now.format('MMM'),
        monthNo: now.format('M'),
      });
      now.subtract(1, 'Month');
    }

    return groupYears.reduce((acc, item) => {
      (acc[item.year] = acc[item.year] || []).push(item);
      return acc;
    }, Object.create(null));
  },

  sortInvoiceArray(invArray) {
    const sortedArray = [];
    const invalidArray = [];

    invArray.forEach((item) => {
      const parsed = parseInt(item.replace('-', ''), 10);
      if (parsed) {
        sortedArray.push({ intVal: parsed, invNo: item });
      } else {
        invalidArray.push({ intVal: '', invNo: item });
      }
    });

    return sortedArray
      .sort((a, b) => a.intVal - b.intVal)
      .concat(invalidArray.sort((x, y) => (x.invNo > y.invNo ? 1 : -1)));
  },

  isNumber(input) {
    const str = String(input);
    return /^-?\d+$/.test(str) || /^\d+\.\d+$/.test(str);
  },

  /** Keypress handler: allow only numbers and one decimal point */
  isNumeric(evt) {
    const keyCode = evt.which || evt.keyCode;

    if (keyCode !== 46 && keyCode > 31 && (keyCode < 48 || keyCode > 57)) {
      evt.preventDefault();
    } else if (keyCode === 46) {
      if ((evt.target.value + '').indexOf('.') !== -1) {
        evt.preventDefault();
      }
    }
  },

  setDefaultValue(value, defaultVal) {
    if (value === undefined || value === null || value.trim() === '') {
      return defaultVal;
    }
    return value;
  },

  /** Keypress handler for version strings (digits + dots) */
  versionFormat(evt) {
    const keyCode = evt.which || evt.keyCode;

    if (keyCode !== 46 && keyCode > 31 && (keyCode < 48 || keyCode > 57)) {
      evt.preventDefault();
    } else if (keyCode === 46) {
      const existingVal = evt.target.value;
      if (existingVal.length === 0 || existingVal[existingVal.length - 1] === '.') {
        evt.preventDefault();
      }
    }
  },

  compareVersions(a, b) {
    const regExStrip0 = /(\.0+)+$/;
    const segmentsA = a.replace(regExStrip0, '').split('.');
    const segmentsB = b.replace(regExStrip0, '').split('.');
    const len = Math.min(segmentsA.length, segmentsB.length);

    for (let i = 0; i < len; i++) {
      const diff = parseInt(segmentsA[i], 10) - parseInt(segmentsB[i], 10);
      if (diff) return diff;
    }
    return segmentsA.length - segmentsB.length;
  },

  groupBy(list, prop) {
    return (list || []).reduce((acc, item) => {
      (acc[item[prop]] = acc[item[prop]] || []).push(item);
      return acc;
    }, {});
  },

  convertToFloat(value, decimalLength = 2) {
    return parseFloat(
      (isNaN(parseFloat(value)) ? 0.00 : parseFloat(value)).toFixed(decimalLength)
    );
  },

  /** Input handler: allow negative numbers with up to 2 decimal places */
  allowNumberWithDecimalNegative(event) {
    const input = event.target;
    const dotCount = input.value.split('.').length;
    const dashCount = input.value.split('-').length;

    if (dotCount > 2) {
      input.value = input.value.substr(0, input.value.length - 1);
    }

    if (dashCount > 2) {
      input.value = input.value.substr(0, input.value.length - 1);
    } else if (input.value.indexOf('-') > 0) {
      input.value = input.value.substr(0, input.value.length - 1);
    }

    input.value = input.value.replace(/[^0-9.\-]/g, '');

    if (dotCount === 2) {
      input.value = input.value.substr(0, input.value.indexOf('.') + 3);
    }

    if (event.keyCode === 8) {
      if (input.value.length === input.value.indexOf('.') + 1) {
        input.value = input.value.substr(0, input.value.length - 1);
      }
    }

    if (input.value === '.') {
      input.value = '';
    }
  },
};

export { utilMethods };
